import { useEffect, useState } from 'react';

interface UseMainPageScrollResult {
  hasScroll: boolean;
  leftPosition: number;
}

interface UseMainPageScrollOptions {
  scrollThreshold?: number;
}

/**
 * Custom hook to detect if the page scroll height is greater than
 * the visible height of the page,
 * and returns the left position of the main content area (kept in sync with
 * unified-nav collapse/expand).
 * @param options - Configuration options
 * @returns {UseMainPageScrollResult} Object with hasScroll boolean and leftPosition number
 */
export const useMainPageScroll = (
  options: UseMainPageScrollOptions = {},
): UseMainPageScrollResult => {
  const { scrollThreshold = 0 } = options;
  const [hasScroll, setHasScroll] = useState(false);
  const [leftPosition, setLeftPosition] = useState(0);

  useEffect(() => {
    // The unified nav animates the secondary menu collapse/expand (Focus Mode)
    // entirely inside its shadow DOM, shifting and resizing the main content area
    // without mutating document.body or resizing the document element. We track
    // the page-content element directly so the fixed footer's left offset stays
    // in sync. We deliberately measure/observe div[slot="pageContent"] rather than
    // z-main-wrapper: the latter is a custom element that defaults to
    // display:inline, so its box shrink-wraps its content and does NOT reliably
    // resize when the nav expands (only when it collapses) — which left the footer
    // stranded behind the nav on expand.
    const contentResizeObserver = new ResizeObserver(scheduleUpdate);
    const shadowObservers = new Map<ShadowRoot, MutationObserver>();
    let observedContentEl: Element | null = null;
    let rafId: number | null = null;

    const getVisiblePageHeight = () => {
      const pageContent = document.querySelector('div[slot="pageContent"]');
      if (pageContent) {
        return pageContent.getBoundingClientRect().bottom + window.scrollY;
      }
      return document.documentElement.scrollHeight;
    };

    const checkScroll = () => {
      const visibleNavHeight = getVisiblePageHeight();
      const scrollHeight =
        visibleNavHeight || document.documentElement.scrollHeight;
      const viewportHeight = document.documentElement.clientHeight;

      setHasScroll(scrollHeight > viewportHeight - scrollThreshold);
    };

    // Watch each nav's shadow root so we re-resolve (and re-observe) the content
    // element if the nav's internal layout is added/replaced or its layout
    // attributes change. The actual collapse animation is tracked by the
    // ResizeObserver on the content element, since the `visible` toggle lives in
    // z-navigation's nested shadow root that a MutationObserver here cannot cross.
    const observeNavShadowRoots = () => {
      const navs = document.querySelectorAll('z-unified-nav-v2');
      for (let i = 0; i < navs.length; i++) {
        const root = navs[i].shadowRoot;
        if (root && !shadowObservers.has(root)) {
          const shadowObserver = new MutationObserver(scheduleUpdate);
          shadowObserver.observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
          });
          shadowObservers.set(root, shadowObserver);
        }
      }
    };

    // Resolve the element whose left edge / width reflect the main content area.
    // Prefer the host's projected page content (a block-level light DOM element
    // that resizes reliably in both directions); fall back to z-main-wrapper's
    // block-level container, then the wrapper itself.
    const resolveContentElement = (): Element | null => {
      const pageContent = document.querySelector('div[slot="pageContent"]');
      if (pageContent) {
        return pageContent;
      }

      const navs = document.querySelectorAll('z-unified-nav-v2');
      for (let i = 0; i < navs.length; i++) {
        const mainWrapper = navs[i].shadowRoot?.querySelector('z-main-wrapper');
        if (mainWrapper) {
          return mainWrapper.parentElement ?? mainWrapper;
        }
      }
      return null;
    };

    const getLeftPosition = () => {
      const contentEl = resolveContentElement();
      if (!contentEl) {
        setLeftPosition(0);
        return;
      }

      // Whole pixels only: this feeds an Emotion dynamic style in LegalFooter, and
      // the cache runs with `speedy: false`, so each distinct value appends another
      // <style> node to the nav shadow root — which the MutationObserver below
      // observes, re-running this measurement. Sub-pixel jitter would self-feed.
      setLeftPosition(Math.round(contentEl.getBoundingClientRect().left));

      if (observedContentEl !== contentEl) {
        if (observedContentEl) {
          contentResizeObserver.unobserve(observedContentEl);
        }
        contentResizeObserver.observe(contentEl);
        observedContentEl = contentEl;
      }
    };

    function scheduleUpdate() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        checkScroll();
        observeNavShadowRoots();
        getLeftPosition();
      });
    }

    // Check on mount
    scheduleUpdate();

    // Check on scroll
    window.addEventListener('scroll', scheduleUpdate);

    // Check on resize
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(document.documentElement);

    /* The host projects its page content in as a light-DOM child of the nav
     * (slotted content has to be a direct child of the shadow host), so watching
     * the nav elements for that child arriving or being swapped is all a
     * MutationObserver is needed for here. Everything else this hook reads is
     * already covered: size changes by the two ResizeObservers, scroll position
     * by the scroll listener, and the nav's internal layout being rebuilt by the
     * shadow-root observers above.
     *
     * This used to observe document.body with subtree + attributes, which meant
     * any class or style change anywhere in the host page — hover, focus, grid
     * virtualisation — scheduled another frame of forced synchronous layout
     * reads. Do not widen it back.
     */
    const observer = new MutationObserver(scheduleUpdate);
    const navElements = document.querySelectorAll('z-unified-nav-v2');
    for (let i = 0; i < navElements.length; i++) {
      observer.observe(navElements[i], { childList: true });
    }

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      resizeObserver.disconnect();
      observer.disconnect();
      contentResizeObserver.disconnect();
      shadowObservers.forEach((shadowObserver) => shadowObserver.disconnect());
      shadowObservers.clear();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = null;
    };
  }, [scrollThreshold]);

  return { hasScroll, leftPosition };
};
